import React, { useCallback } from "react";
import { observer } from "mobx-react-lite";

import { CreateCheckoutSessionInput } from "../../../../../api-models/request/CreateCheckoutSessionInput";
import {
  OrganizationSubscriptionPlansStorage,
  SubscriptionPlanSelection,
} from "../../../../../services/organization-subscription-plans/OrganizationSubscriptionPlansStorage";
import { SubscriptionPlan } from "../../../../../services/organization-subscription-plans/SubscriptionPlan";
import { appSessionStorage } from "../../../../../services/storage/SessionStorageService";
import { currentUserService } from "../../../../../services/user/CurrentUserStorage";
import useErrorHandler, {
  ApplicationError,
} from "../../../../../services/error-handler/ErrorHandlerService";
import useThemeService from "../../../../../services/theme-service/ThemeService";
import Button from "../../../../../components/button/Button";
import { warningLeavePageService } from "../../../../../services/page/WarningLeavePageService";
import GenericSubscriptionPlanInput from "../../../../../api-models/request/GenericSubscriptionPlanInput";
import WarningBeforeAction from "../../../../../components/data-components/WarningBeforeAction";
import useModal from "../../../../../components/dialog/useModal";

interface SubscribeButtonProps {
  seatsAmount: number;
  extraStorageAmount: number;
  extraVACallsAmount: number;
  subscriptionPlan: SubscriptionPlan;
  organizationSubscriptionPlansStorage: OrganizationSubscriptionPlansStorage;
  confirmationButtonTextKey: string;
  confirmationModalTitleKey: string;
  onValidationActionClick: () => Promise<boolean>;
  children?: any;
}

export const SubscribeButton = observer((props: SubscribeButtonProps) => {
  const { themeClass } = useThemeService();
  const { handleErrorInSnackbar } = useErrorHandler();
  const { toggleModal, isVisible } = useModal();

  const {
    seatsAmount,
    extraStorageAmount,
    extraVACallsAmount,
    subscriptionPlan,
    organizationSubscriptionPlansStorage,
    confirmationButtonTextKey,
    confirmationModalTitleKey,
    onValidationActionClick,
  } = props;

  const handleCanceledSubscriptionReactivation = async () => {
    warningLeavePageService.setIsSubmitting(true);
    try {
      const subscriptionPlanSelection: GenericSubscriptionPlanInput = {
        packageId: subscriptionPlan.id,
        sewerInspectionModuleId: subscriptionPlan.sewerInspectionModule!.id,
        fullRightSeats: seatsAmount,
        extraStorage: extraStorageAmount,
        extraVACalls: extraVACallsAmount,
      };
      await organizationSubscriptionPlansStorage.withdrawCancelationRequest(
        subscriptionPlanSelection
      );
    } catch (error) {
      const applicationError = error as ApplicationError;
      handleErrorInSnackbar(applicationError);
    }

    toggleModal();
    warningLeavePageService.reset();
  };

  const handleSubscribeClick = useCallback(async () => {
    if (organizationSubscriptionPlansStorage.subscriptionWillBeCanceled) {
      if (subscriptionPlan.id < organizationSubscriptionPlansStorage.currentActiveSubscription.id) {
        return toggleModal();
      }

      const isValid = await onValidationActionClick();
      if (isValid) return toggleModal();
      return;
    }

    const isValid = await onValidationActionClick();
    if (!isValid) return;

    warningLeavePageService.setIsSubmitting(true);
    const input: CreateCheckoutSessionInput = {
      subscriptionPlanId: subscriptionPlan.id,
      subscriptionPlanAmount: seatsAmount,
      sewerInspectionModuleId: subscriptionPlan.sewerInspectionModule!.id,
      extraStoragePackageId: subscriptionPlan.extraStoragePackage!.id,
      extraStoragePackageAmount: extraStorageAmount,
      extraVACallsPackageId: subscriptionPlan.extraVACalls!.id,
      extraVACallsPackageAmount: extraVACallsAmount,
      organizationId: currentUserService.currentUser.currentOrganization.id,
    };
    try {
      var sessionUrl = await organizationSubscriptionPlansStorage.getCheckoutSessionUrl(input);
      const subscriptionPlanSelection: SubscriptionPlanSelection = {
        planId: subscriptionPlan.id.toString(),
        seatsAmount: seatsAmount.toString(),
        extraStorageTBAmount: extraStorageAmount.toString(),
        extraVACallsPackagesAmount: extraVACallsAmount.toString(),
      };
      appSessionStorage.setStorageItem(
        "SubscriptionPlanSelection",
        JSON.stringify(subscriptionPlanSelection)
      );
      window.location.href = sessionUrl;
      warningLeavePageService.reset();
    } catch (error) {
      warningLeavePageService.setIsSubmitting(false);
      const applicationError = error as ApplicationError;
      handleErrorInSnackbar(applicationError);
    }
  }, [
    extraStorageAmount,
    extraVACallsAmount,
    handleErrorInSnackbar,
    onValidationActionClick,
    organizationSubscriptionPlansStorage,
    seatsAmount,
    subscriptionPlan.extraStoragePackage,
    subscriptionPlan.extraVACalls,
    subscriptionPlan.id,
    subscriptionPlan.sewerInspectionModule,
    toggleModal,
  ]);

  return (
    <>
      <Button
        disabled={currentUserService.hasCurrentOrganizationVatWarning}
        className={themeClass(["btn-big"])}
        textKey="SubscriptionPlans.card.subscribe"
        onClick={handleSubscribeClick}
      />
      <WarningBeforeAction
        isVisible={isVisible}
        titleKey={confirmationModalTitleKey}
        buttonOk={{
          id: "action-button-id",
          textKey: confirmationButtonTextKey,
          action: handleCanceledSubscriptionReactivation,
        }}
        toggleModal={toggleModal}
      >
        {props.children}
      </WarningBeforeAction>
    </>
  );
});
